/**
 * Banner — the single section/page-level notice strip (see DESIGN.md "Banners").
 *
 * Variant fixes the colour semantics AND the aria role so they cannot drift:
 * error announces assertively (role="alert"); warning/info announce politely
 * (role="status"). A success is not a banner: it is a toast (Toast.tsx).
 *
 * Dismissal is the caller's business: passing onDismiss renders the X, but
 * whether that dismiss is ephemeral (local state) or acknowledged (persisted
 * via API) is decided — and documented — at the call site.
 */
import type { ReactNode } from "react";
import { AlertTriangleIcon, InfoIcon, XIcon } from "./icons";

import styles from "./Banner.module.css";

export type BannerVariant = "info" | "warning" | "error";

interface BannerProps {
  variant: BannerVariant;
  /** Bold first line. Optional — short banners can be body-only children. */
  title?: string;
  children?: ReactNode;
  /** Renders the X button. The caller owns what dismissal means/persists. */
  onDismiss?: () => void;
  /** Accessible label for the X button. */
  dismissLabel?: string;
  /** Disables the X while a persisted dismiss is in flight. */
  dismissing?: boolean;
  className?: string;
  "data-testid"?: string;
}

function defaultIcon(variant: BannerVariant, className: string): ReactNode {
  switch (variant) {
    case "info":
      return <InfoIcon className={className} />;
    case "warning":
    case "error":
      return <AlertTriangleIcon className={className} />;
  }
}

export function Banner({
  variant,
  title,
  children,
  onDismiss,
  dismissLabel = "Dismiss",
  dismissing = false,
  className = "",
  "data-testid": testId,
}: BannerProps) {
  // The variant's colours are one class on the container; the parts below
  // take theirs by descent (see Banner.module.css).
  const iconNode = defaultIcon(variant, styles.icon);

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={`${styles.banner} ${styles[variant]} ${className}`}
      data-testid={testId}
    >
      <div className={styles.row}>
        {iconNode}
        <div className={styles.body}>
          {title && <p className={styles.title}>{title}</p>}
          {children}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            disabled={dismissing}
            aria-label={dismissLabel}
            className={styles.dismiss}
          >
            <XIcon className={styles.dismissIcon} />
          </button>
        )}
      </div>
    </div>
  );
}
